import Form from '@rjsf/shadcn';
import validator from '@rjsf/validator-ajv8';

import { OffersProvider, ProductsProvider, StoreConfigProvider } from '@/components/config-editor/providers';
import { fields, widgets } from '@/data/rjsf';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';

// Native checkout schema with full translation fields
const nativeCheckoutSchemaComplete: RJSFSchema = {
  title: 'Native Checkout Configuration',
  type: 'object' as const,
  properties: {
    configName: {
      type: 'string',
      title: 'Config Name',
      default: 'default',
    },
    isDebugMode: {
      type: 'boolean',
      title: 'Debug Mode',
      default: false,
    },
    precheckMarketing: {
      type: 'boolean',
      title: 'Precheck Marketing',
      default: false,
    },
    branding: {
      type: 'object',
      title: 'Branding',
      properties: {
        companyName: {
          type: 'string',
          title: 'Company Name',
          default: 'Native Checkout Demo',
        },
        primaryColor: {
          type: 'string',
          title: 'Primary Color',
          format: 'color',
          default: '#ef4444',
        },
        fontFamily: {
          type: 'string',
          title: 'Font Family',
          enum: ['inter', 'roboto', 'helvetica', 'arial'],
          default: 'inter',
        },
        logoUrl: {
          type: 'string',
          title: 'Logo URL',
          format: 'storeConfigImage',
        },
      },
    },
    offerId: {
      type: 'string',
      title: 'Offer ID',
      format: 'offerSelector',
    },
    products: {
      type: 'object',
      title: 'Products',
      properties: {
        variantId: {
          type: 'string',
          title: 'Variant ID',
          format: 'variantSelector',
        },
      },
    },
    layout: {
      type: 'object',
      title: 'Layout',
      properties: {
        desktopMargin: {
          type: 'string',
          title: 'Desktop Margin',
          enum: ['small', 'medium', 'large'],
          default: 'large',
        },
        stickyOrderSummary: {
          type: 'boolean',
          title: 'Sticky Order Summary',
          default: false,
        },
        stickyPaymentButtons: {
          type: 'boolean',
          title: 'Sticky Payment Buttons',
          default: true,
        },
      },
    },
    checkout: {
      type: 'object',
      title: 'Checkout',
      properties: {
        showExpressCheckout: {
          type: 'boolean',
          title: 'Show Express Checkout',
          default: true,
        },
        hideRecurringPrices: {
          type: 'boolean',
          title: 'Hide Recurring Prices',
          default: false,
        },
      },
    },
    texts: {
      type: 'object',
      title: 'Text Translations',
      properties: {
        sections: {
          type: 'object',
          title: 'Sections',
          properties: {
            title: {
              type: 'object',
              title: 'Section Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Why choose us?' },
                fr: { type: 'string', title: 'French', default: 'Pourquoi nous choisir?' },
              },
            },
          },
        },
        checkoutTitle: {
          type: 'object',
          title: 'Checkout Title',
          format: 'translation',
          properties: {
            en: { type: 'string', title: 'English', default: 'Checkout' },
            fr: { type: 'string', title: 'French', default: 'Commande' },
          },
        },
        topBar: {
          type: 'object',
          title: 'Top Bar',
          properties: {
            goToShopText: {
              type: 'object',
              title: 'Go to Shop Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Go to Shop' },
                fr: { type: 'string', title: 'French', default: 'Aller à la boutique' },
              },
            },
          },
        },
        expressCheckout: {
          type: 'object',
          title: 'Express Checkout',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Express checkout' },
                fr: { type: 'string', title: 'French', default: 'Commande express' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            googlePayButton: {
              type: 'object',
              title: 'Google Pay Button',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay with Google Pay' },
                fr: { type: 'string', title: 'French', default: 'Payer avec Google Pay' },
              },
            },
            paypalButton: {
              type: 'object',
              title: 'PayPal Button',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay with PayPal' },
                fr: { type: 'string', title: 'French', default: 'Payer avec PayPal' },
              },
            },
            applePayButton: {
              type: 'object',
              title: 'Apple Pay Button',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay with Apple Pay' },
                fr: { type: 'string', title: 'French', default: 'Payer avec Apple Pay' },
              },
            },
            dividerText: {
              type: 'object',
              title: 'Divider Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Or' },
                fr: { type: 'string', title: 'French', default: 'Ou' },
              },
            },
          },
        },
        contact: {
          type: 'object',
          title: 'Contact',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Contact' },
                fr: { type: 'string', title: 'French', default: 'Contact' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            emailPlaceholder: {
              type: 'object',
              title: 'Email Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Email' },
                fr: { type: 'string', title: 'French', default: 'E-mail' },
              },
            },
            emailOffersLabel: {
              type: 'object',
              title: 'Email Offers Label',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
          },
        },
        delivery: {
          type: 'object',
          title: 'Delivery',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Delivery' },
                fr: { type: 'string', title: 'French', default: 'Livraison' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            countryPlaceholder: {
              type: 'object',
              title: 'Country Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Country/Region' },
                fr: { type: 'string', title: 'French', default: 'Pays/Région' },
              },
            },
            firstNamePlaceholder: {
              type: 'object',
              title: 'First Name Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'First name' },
                fr: { type: 'string', title: 'French', default: 'Prénom' },
              },
            },
            lastNamePlaceholder: {
              type: 'object',
              title: 'Last Name Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Last name' },
                fr: { type: 'string', title: 'French', default: 'Nom de famille' },
              },
            },
            addressPlaceholder: {
              type: 'object',
              title: 'Address Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Address' },
                fr: { type: 'string', title: 'French', default: 'Adresse' },
              },
            },
            cityPlaceholder: {
              type: 'object',
              title: 'City Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'City' },
                fr: { type: 'string', title: 'French', default: 'Ville' },
              },
            },
            statePlaceholder: {
              type: 'object',
              title: 'State Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'State' },
                fr: { type: 'string', title: 'French', default: 'État/Province' },
              },
            },
            zipPlaceholder: {
              type: 'object',
              title: 'ZIP Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'ZIP code' },
                fr: { type: 'string', title: 'French', default: 'Code postal' },
              },
            },
            billingTitle: {
              type: 'object',
              title: 'Billing Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Billing address' },
                fr: { type: 'string', title: 'French', default: 'Adresse de facturation' },
              },
            },
          },
        },
        shipping: {
          type: 'object',
          title: 'Shipping',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Shipping method' },
                fr: { type: 'string', title: 'French', default: 'Méthode de livraison' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            freeText: {
              type: 'object',
              title: 'Free Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Free' },
                fr: { type: 'string', title: 'French', default: 'Gratuit' },
              },
            },
            noRatesText: {
              type: 'object',
              title: 'No Rates Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
          },
        },
        payment: {
          type: 'object',
          title: 'Payment',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Payment' },
                fr: { type: 'string', title: 'French', default: 'Paiement' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            creditCardLabel: {
              type: 'object',
              title: 'Credit Card Label',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Credit card' },
                fr: { type: 'string', title: 'French', default: 'Carte de crédit' },
              },
            },
            cardNumberPlaceholder: {
              type: 'object',
              title: 'Card Number Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Card number' },
                fr: { type: 'string', title: 'French', default: 'Numéro de carte' },
              },
            },
            expiryPlaceholder: {
              type: 'object',
              title: 'Expiry Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'MM / YY' },
                fr: { type: 'string', title: 'French', default: 'MM / AA' },
              },
            },
            securityCodePlaceholder: {
              type: 'object',
              title: 'Security Code Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Security code' },
                fr: { type: 'string', title: 'French', default: 'Code de sécurité' },
              },
            },
            creditCard: {
              type: 'object',
              title: 'Credit Card',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Credit Card' },
                fr: { type: 'string', title: 'French', default: 'Carte de crédit' },
              },
            },
            creditCardSecureText: {
              type: 'object',
              title: 'Credit Card Secure Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            completePaymentText: {
              type: 'object',
              title: 'Complete Payment Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
          },
        },
        orderCompletion: {
          type: 'object',
          title: 'Order Completion',
          properties: {
            processingText: {
              type: 'object',
              title: 'Processing Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Processing...' },
                fr: { type: 'string', title: 'French', default: 'Traitement en cours...' },
              },
            },
            completeOrderText: {
              type: 'object',
              title: 'Complete Order Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Complete order' },
                fr: { type: 'string', title: 'French', default: 'Finaliser la commande' },
              },
            },
            secureCheckoutText: {
              type: 'object',
              title: 'Secure Checkout Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            loadingText: {
              type: 'object',
              title: 'Loading Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Loading...' },
                fr: { type: 'string', title: 'French', default: 'Chargement...' },
              },
            },
            payWithVipText: {
              type: 'object',
              title: 'Pay with VIP Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay' },
                fr: { type: 'string', title: 'French', default: 'Payer' },
              },
            },
            selectVipOffer: {
              type: 'object',
              title: 'Select VIP Offer',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay as VIP' },
                fr: { type: 'string', title: 'French', default: 'Payer en VIP' },
              },
            },
            cancelVipOffer: {
              type: 'object',
              title: 'Cancel VIP Offer',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Cancel VIP offer' },
                fr: { type: 'string', title: 'French', default: "Annuler l'offre VIP" },
              },
            },
            payWithoutVipText: {
              type: 'object',
              title: 'Pay without VIP Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay without VIP' },
                fr: { type: 'string', title: 'French', default: 'Payer sans VIP' },
              },
            },
            applePayText: {
              type: 'object',
              title: 'Apple Pay Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Pay with Apple Pay' },
                fr: { type: 'string', title: 'French', default: 'Payer avec Apple Pay' },
              },
            },
          },
        },
        orderBumps: {
          type: 'object',
          title: 'Order Bumps',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Add to your order' },
                fr: { type: 'string', title: 'French', default: 'Ajouter à votre commande' },
              },
            },
            description: {
              type: 'object',
              title: 'Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            secondaryTitle: {
              type: 'object',
              title: 'Secondary Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            secondaryDescription: {
              type: 'object',
              title: 'Secondary Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            vipTitle: {
              type: 'object',
              title: 'VIP Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Club offer' },
                fr: { type: 'string', title: 'French', default: 'Offre club' },
              },
            },
            vipDescription: {
              type: 'object',
              title: 'VIP Description',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English' },
                fr: { type: 'string', title: 'French' },
              },
            },
            vipType: {
              type: 'string',
              title: 'VIP Type',
              enum: ['default', 'premium', 'exclusive'],
              default: 'default',
            },
            appliedText: {
              type: 'object',
              title: 'Applied Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Applied' },
                fr: { type: 'string', title: 'French', default: 'Appliqué' },
              },
            },
            congratulationsText: {
              type: 'object',
              title: 'Congratulations Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Congratulations' },
                fr: { type: 'string', title: 'French', default: 'Félicitations' },
              },
            },
          },
        },
        orderSummary: {
          type: 'object',
          title: 'Order Summary',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Order summary' },
                fr: { type: 'string', title: 'French', default: 'Résumé de la commande' },
              },
            },
            subtotalText: {
              type: 'object',
              title: 'Subtotal Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Subtotal' },
                fr: { type: 'string', title: 'French', default: 'Sous-total' },
              },
            },
            itemText: {
              type: 'object',
              title: 'Item Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'item' },
                fr: { type: 'string', title: 'French', default: 'article' },
              },
            },
            itemsText: {
              type: 'object',
              title: 'Items Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'items' },
                fr: { type: 'string', title: 'French', default: 'articles' },
              },
            },
            totalSavingsText: {
              type: 'object',
              title: 'Total Savings Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Total Savings' },
                fr: { type: 'string', title: 'French', default: 'Économies totales' },
              },
            },
            shippingText: {
              type: 'object',
              title: 'Shipping Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Shipping' },
                fr: { type: 'string', title: 'French', default: 'Livraison' },
              },
            },
            taxText: {
              type: 'object',
              title: 'Tax Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Estimated taxes' },
                fr: { type: 'string', title: 'French', default: 'Taxes estimées' },
              },
            },
            totalText: {
              type: 'object',
              title: 'Total Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Total' },
                fr: { type: 'string', title: 'French', default: 'Total' },
              },
            },
            recurringTotalText: {
              type: 'object',
              title: 'Recurring Total Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Recurring total' },
                fr: { type: 'string', title: 'French', default: 'Total récurrent' },
              },
            },
            everyText: {
              type: 'object',
              title: 'Every Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'every' },
                fr: { type: 'string', title: 'French', default: 'chaque' },
              },
            },
          },
        },
        validation: {
          type: 'object',
          title: 'Validation',
          properties: {
            emailRequired: {
              type: 'object',
              title: 'Email Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Valid email is required' },
                fr: { type: 'string', title: 'French', default: 'Un e-mail valide est requis' },
              },
            },
            firstNameRequired: {
              type: 'object',
              title: 'First Name Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'First name is required' },
                fr: { type: 'string', title: 'French', default: 'Le prénom est requis' },
              },
            },
            lastNameRequired: {
              type: 'object',
              title: 'Last Name Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Last name is required' },
                fr: { type: 'string', title: 'French', default: 'Le nom de famille est requis' },
              },
            },
            addressRequired: {
              type: 'object',
              title: 'Address Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Address is required' },
                fr: { type: 'string', title: 'French', default: "L'adresse est requise" },
              },
            },
            cityRequired: {
              type: 'object',
              title: 'City Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'City is required' },
                fr: { type: 'string', title: 'French', default: 'La ville est requise' },
              },
            },
            countryRequired: {
              type: 'object',
              title: 'Country Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Country is required' },
                fr: { type: 'string', title: 'French', default: 'Le pays est requis' },
              },
            },
            stateRequired: {
              type: 'object',
              title: 'State Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'State is required' },
                fr: { type: 'string', title: 'French', default: "L'état/province est requis" },
              },
            },
            zipRequired: {
              type: 'object',
              title: 'ZIP Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'ZIP code is required' },
                fr: { type: 'string', title: 'French', default: 'Le code postal est requis' },
              },
            },
            cardNumberRequired: {
              type: 'object',
              title: 'Card Number Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Valid card number is required' },
                fr: { type: 'string', title: 'French', default: 'Un numéro de carte valide est requis' },
              },
            },
            expiryRequired: {
              type: 'object',
              title: 'Expiry Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Valid expiry date (MM/YY) is required' },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: "Une date d'expiration valide (MM/AA) est requise",
                },
              },
            },
            securityCodeRequired: {
              type: 'object',
              title: 'Security Code Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Valid security code is required' },
                fr: { type: 'string', title: 'French', default: 'Un code de sécurité valide est requis' },
              },
            },
            shippingMethodRequired: {
              type: 'object',
              title: 'Shipping Method Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Shipping method is required' },
                fr: { type: 'string', title: 'French', default: 'La méthode de livraison est requise' },
              },
            },
            paymentMethodRequired: {
              type: 'object',
              title: 'Payment Method Required',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Payment method is required' },
                fr: { type: 'string', title: 'French', default: 'La méthode de paiement est requise' },
              },
            },
          },
        },
        errors: {
          type: 'object',
          title: 'Errors',
          properties: {
            checkoutSessionNotInitialized: {
              type: 'object',
              title: 'Checkout Session Not Initialized',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Checkout session not initialized' },
                fr: { type: 'string', title: 'French', default: 'Session de commande non initialisée' },
              },
            },
            checkoutSessionNotReady: {
              type: 'object',
              title: 'Checkout Session Not Ready',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Checkout session not ready' },
                fr: { type: 'string', title: 'French', default: 'Session de commande pas prête' },
              },
            },
            paymentFailed: {
              type: 'object',
              title: 'Payment Failed',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Payment failed' },
                fr: { type: 'string', title: 'French', default: 'Le paiement a échoué' },
              },
            },
            checkoutError: {
              type: 'object',
              title: 'Checkout Error',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'An error occurred during checkout' },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: "Une erreur s'est produite lors de la commande",
                },
              },
            },
            failedToLoadAddressDetails: {
              type: 'object',
              title: 'Failed to Load Address Details',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Failed to load address details' },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: "Échec du chargement des détails d'adresse",
                },
              },
            },
            errorLoadingOrderSummary: {
              type: 'object',
              title: 'Error Loading Order Summary',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Error loading order summary' },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: 'Erreur lors du chargement du résumé de commande',
                },
              },
            },
            unableToLoadOrderInfo: {
              type: 'object',
              title: 'Unable to Load Order Info',
              format: 'translation',
              properties: {
                en: {
                  type: 'string',
                  title: 'English',
                  default: 'Unable to load order information. Please try again.',
                },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: 'Impossible de charger les informations de commande. Veuillez réessayer.',
                },
              },
            },
            applePayNotImplemented: {
              type: 'object',
              title: 'Apple Pay Not Implemented',
              format: 'translation',
              properties: {
                en: {
                  type: 'string',
                  title: 'English',
                  default: 'Apple Pay support needs to be implemented',
                },
                fr: {
                  type: 'string',
                  title: 'French',
                  default: 'Le support Apple Pay doit être implémenté',
                },
              },
            },
          },
        },
        loading: {
          type: 'object',
          title: 'Loading',
          properties: {
            checkoutLoading: {
              type: 'object',
              title: 'Checkout Loading',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Loading checkout...' },
                fr: { type: 'string', title: 'French', default: 'Chargement de la commande...' },
              },
            },
          },
        },
        discountCodes: {
          type: 'object',
          title: 'Discount Codes',
          properties: {
            placeholder: {
              type: 'object',
              title: 'Placeholder',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Discount code' },
                fr: { type: 'string', title: 'French', default: 'Code de réduction' },
              },
            },
            applyingText: {
              type: 'object',
              title: 'Applying Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Applying...' },
                fr: { type: 'string', title: 'French', default: 'Application...' },
              },
            },
            applyText: {
              type: 'object',
              title: 'Apply Text',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Apply' },
                fr: { type: 'string', title: 'French', default: 'Appliquer' },
              },
            },
          },
        },
        thankYou: {
          type: 'object',
          title: 'Thank You',
          properties: {
            title: {
              type: 'object',
              title: 'Title',
              format: 'translation',
              properties: {
                en: { type: 'string', title: 'English', default: 'Thank you for your order!' },
                fr: { type: 'string', title: 'French', default: 'Merci pour votre commande !' },
              },
            },
          },
        },
      },
    },
  },
};

const nativeCheckoutUiSchemaComplete: UiSchema = {
  // Grouping metadata: tabs and sections configuration
  'ui:grouping': {
    tabs: [
      { id: 'settings', title: 'Settings' },
      { id: 'content', title: 'Content' },
    ],
    sections: [
      { id: 'general', title: 'General Settings', tabId: 'settings' },
      { id: 'branding', title: 'Branding', tabId: 'settings' },
      { id: 'products', title: 'Products & Offers', tabId: 'settings' },
      { id: 'layout', title: 'Layout', tabId: 'settings' },
      { id: 'checkout', title: 'Checkout Options', tabId: 'settings' },
      { id: 'texts', title: 'Texts & Translations', tabId: 'content' },
    ],
  },
  // Field assignments to tabs and sections
  configName: {
    'ui:tab': 'settings',
    'ui:section': 'general',
  },
  isDebugMode: {
    'ui:tab': 'settings',
    'ui:section': 'general',
  },
  precheckMarketing: {
    'ui:tab': 'settings',
    'ui:section': 'general',
  },
  branding: {
    'ui:tab': 'settings',
    'ui:section': 'branding',
    logoUrl: {
      'ui:widget': 'storeConfigImage',
    },
  },
  products: {
    'ui:tab': 'settings',
    'ui:section': 'products',
    variantId: {
      'ui:widget': 'variantSelector',
    },
  },
  offerId: {
    'ui:tab': 'settings',
    'ui:section': 'products',
  },
  layout: {
    'ui:tab': 'settings',
    'ui:section': 'layout',
  },
  checkout: {
    'ui:tab': 'settings',
    'ui:section': 'checkout',
  },
  texts: {
    'ui:tab': 'content',
    'ui:section': 'texts',
    sections: {
      title: {
        'ui:field': 'translation',
      },
    },
    checkoutTitle: {
      'ui:field': 'translation',
    },
    topBar: {
      goToShopText: {
        'ui:field': 'translation',
      },
    },
    expressCheckout: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      googlePayButton: {
        'ui:field': 'translation',
      },
      paypalButton: {
        'ui:field': 'translation',
      },
      applePayButton: {
        'ui:field': 'translation',
      },
      dividerText: {
        'ui:field': 'translation',
      },
    },
    contact: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      emailPlaceholder: {
        'ui:field': 'translation',
      },
      emailOffersLabel: {
        'ui:field': 'translation',
      },
    },
    delivery: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      countryPlaceholder: {
        'ui:field': 'translation',
      },
      firstNamePlaceholder: {
        'ui:field': 'translation',
      },
      lastNamePlaceholder: {
        'ui:field': 'translation',
      },
      addressPlaceholder: {
        'ui:field': 'translation',
      },
      cityPlaceholder: {
        'ui:field': 'translation',
      },
      statePlaceholder: {
        'ui:field': 'translation',
      },
      zipPlaceholder: {
        'ui:field': 'translation',
      },
      billingTitle: {
        'ui:field': 'translation',
      },
    },
    shipping: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      freeText: {
        'ui:field': 'translation',
      },
      noRatesText: {
        'ui:field': 'translation',
      },
    },
    payment: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      creditCardLabel: {
        'ui:field': 'translation',
      },
      cardNumberPlaceholder: {
        'ui:field': 'translation',
      },
      expiryPlaceholder: {
        'ui:field': 'translation',
      },
      securityCodePlaceholder: {
        'ui:field': 'translation',
      },
      creditCard: {
        'ui:field': 'translation',
      },
      creditCardSecureText: {
        'ui:field': 'translation',
      },
      completePaymentText: {
        'ui:field': 'translation',
      },
    },
    orderCompletion: {
      processingText: {
        'ui:field': 'translation',
      },
      completeOrderText: {
        'ui:field': 'translation',
      },
      secureCheckoutText: {
        'ui:field': 'translation',
      },
      loadingText: {
        'ui:field': 'translation',
      },
      payWithVipText: {
        'ui:field': 'translation',
      },
      selectVipOffer: {
        'ui:field': 'translation',
      },
      cancelVipOffer: {
        'ui:field': 'translation',
      },
      payWithoutVipText: {
        'ui:field': 'translation',
      },
      applePayText: {
        'ui:field': 'translation',
      },
    },
    orderBumps: {
      title: {
        'ui:field': 'translation',
      },
      description: {
        'ui:field': 'translation',
      },
      secondaryTitle: {
        'ui:field': 'translation',
      },
      secondaryDescription: {
        'ui:field': 'translation',
      },
      vipTitle: {
        'ui:field': 'translation',
      },
      vipDescription: {
        'ui:field': 'translation',
      },
      appliedText: {
        'ui:field': 'translation',
      },
      congratulationsText: {
        'ui:field': 'translation',
      },
    },
    orderSummary: {
      title: {
        'ui:field': 'translation',
      },
      subtotalText: {
        'ui:field': 'translation',
      },
      itemText: {
        'ui:field': 'translation',
      },
      itemsText: {
        'ui:field': 'translation',
      },
      totalSavingsText: {
        'ui:field': 'translation',
      },
      shippingText: {
        'ui:field': 'translation',
      },
      taxText: {
        'ui:field': 'translation',
      },
      totalText: {
        'ui:field': 'translation',
      },
      recurringTotalText: {
        'ui:field': 'translation',
      },
      everyText: {
        'ui:field': 'translation',
      },
    },
    validation: {
      emailRequired: {
        'ui:field': 'translation',
      },
      firstNameRequired: {
        'ui:field': 'translation',
      },
      lastNameRequired: {
        'ui:field': 'translation',
      },
      addressRequired: {
        'ui:field': 'translation',
      },
      cityRequired: {
        'ui:field': 'translation',
      },
      countryRequired: {
        'ui:field': 'translation',
      },
      stateRequired: {
        'ui:field': 'translation',
      },
      zipRequired: {
        'ui:field': 'translation',
      },
      cardNumberRequired: {
        'ui:field': 'translation',
      },
      expiryRequired: {
        'ui:field': 'translation',
      },
      securityCodeRequired: {
        'ui:field': 'translation',
      },
      shippingMethodRequired: {
        'ui:field': 'translation',
      },
      paymentMethodRequired: {
        'ui:field': 'translation',
      },
    },
    errors: {
      checkoutSessionNotInitialized: {
        'ui:field': 'translation',
      },
      checkoutSessionNotReady: {
        'ui:field': 'translation',
      },
      paymentFailed: {
        'ui:field': 'translation',
      },
      checkoutError: {
        'ui:field': 'translation',
      },
      failedToLoadAddressDetails: {
        'ui:field': 'translation',
      },
      errorLoadingOrderSummary: {
        'ui:field': 'translation',
      },
      unableToLoadOrderInfo: {
        'ui:field': 'translation',
      },
      applePayNotImplemented: {
        'ui:field': 'translation',
      },
    },
    loading: {
      checkoutLoading: {
        'ui:field': 'translation',
      },
    },
    discountCodes: {
      placeholder: {
        'ui:field': 'translation',
      },
      applyingText: {
        'ui:field': 'translation',
      },
      applyText: {
        'ui:field': 'translation',
      },
    },
    thankYou: {
      title: {
        'ui:field': 'translation',
      },
    },
  },
};

function ConfigEditor() {
  return (
    <ProductsProvider>
      <StoreConfigProvider>
        <OffersProvider>
          <div className="min-h-screen overflow-auto p-8">
            <Form
              className="w-full"
              schema={nativeCheckoutSchemaComplete}
              uiSchema={nativeCheckoutUiSchemaComplete}
              validator={validator}
              widgets={widgets}
              fields={fields}
              onChange={(e) => {
                console.log('Form onChange - full formData:', e.formData);
              }}
              onSubmit={(e) => console.log('Form submit - formData:', e.formData)}
            />
          </div>
        </OffersProvider>
      </StoreConfigProvider>
    </ProductsProvider>
  );
}

export default ConfigEditor;
export { nativeCheckoutSchemaComplete, nativeCheckoutUiSchemaComplete };
